import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Tenant } from '@mg/shared';
import { api } from '../lib/api';
import { getActiveTenantSlug, setActiveTenantSlug } from './activeTenant';
import { isWorkspaceAgnosticRoute } from '../components/shell/accessRoutes';

// Install-level and per-user routes come from accessRoutes, shared with the no-workspace gate
// in WorkspaceGate: a page where a ?ws= is meaningless must not require a workspace to exist, and
// two lists of the same thing drift.

interface TenantContextValue {
  /** The user's Brand Workspaces (empty for a brand-new user). */
  tenants: Tenant[];
  /** The active Brand Workspace, or null when the user has none yet. */
  activeTenant: Tenant | null;
  /** True once the user has at least one workspace. Gates the app. */
  hasWorkspace: boolean;
  isLoading: boolean;
  error: string | null;
  /** Make a workspace active by slug (updates the X-Tenant header). */
  setActiveTenant: (slug: string) => void;
  /** Re-fetch the workspace list (after create/edit) and return it. */
  refresh: () => Promise<Tenant[]>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Loads the user's Brand Workspaces (DB model: Tenant) and tracks the active one.
 * Selecting a workspace updates the X-Tenant header source and invalidates
 * workspace-scoped queries so every page refetches for the newly selected one.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const workspaceAgnostic = isWorkspaceAgnosticRoute(pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSlug = searchParams.get('ws');
  // react-router recreates setSearchParams whenever the query string changes; hold it in a ref so
  // setActiveTenant + the context value stay identity-stable across UNRELATED ?param navigations
  // (composer modal, view toggles) instead of re-rendering every useTenant() consumer.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tenants'],
    queryFn: ({ signal }) => api.listTenants(signal),
  });

  const tenants = useMemo(() => data ?? [], [data]);

  // The active workspace is derived from the URL (`?ws=<slug>`) so it shows in the address bar and
  // is shareable/bookmarkable. Resolution order: a VALID `?ws` wins (deep links), else the stored
  // slug, else the first workspace. localStorage is read directly here; the reconcile effect keeps
  // it in sync with what we resolved.
  const activeSlug = useMemo(() => {
    if (tenants.length === 0) return null;
    const has = (s: string | null) => !!s && tenants.some((t) => t.slug === s);
    if (has(urlSlug)) return urlSlug;
    const stored = getActiveTenantSlug();
    if (has(stored)) return stored;
    return tenants[0].slug;
  }, [tenants, urlSlug]);

  // Reconcile the URL, the X-Tenant header source (the localStorage holder), and the query cache
  // with the resolved active workspace. Self-terminating — it stops once `?ws === activeSlug`.
  useEffect(() => {
    if (data === undefined) return; // wait for the workspace list — don't strip a deep-linked ?ws
    if (tenants.length === 0) {
      // Brand-new user / all workspaces deleted: clear the holder so the gate engages.
      if (getActiveTenantSlug() !== null) setActiveTenantSlug(null);
      return;
    }
    // The header source changed (a switch, a deep-link, or the active workspace being deleted) →
    // sync it and refetch every workspace-scoped query under the correct tenant.
    if (getActiveTenantSlug() !== activeSlug) {
      setActiveTenantSlug(activeSlug);
      void queryClient.invalidateQueries();
    }
    // Reflect the active workspace in the URL — EXCEPT on install-level / per-user pages that span
    // or ignore workspaces (see WORKSPACE_AGNOSTIC_ROUTES), where a ?ws would mislead. There, strip
    // any stray ?ws; elsewhere, add it after a param-less nav or correct an unknown/stale slug.
    // `replace` so it leaves no junk history entry.
    if (workspaceAgnostic) {
      if (urlSlug !== null) {
        setSearchParamsRef.current(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('ws');
            return next;
          },
          { replace: true },
        );
      }
    } else if (activeSlug && urlSlug !== activeSlug) {
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('ws', activeSlug);
          return next;
        },
        { replace: true },
      );
    }
  }, [data, tenants, activeSlug, urlSlug, workspaceAgnostic, queryClient]);

  const setActiveTenant = useCallback(
    (slug: string) => {
      if (slug === activeSlug) return;
      // Push `?ws=<slug>`; the reconcile effect syncs the header holder + invalidates the cache.
      // Push (not replace) so browser Back returns to the previously-active workspace.
      setSearchParamsRef.current((prev) => {
        const next = new URLSearchParams(prev);
        next.set('ws', slug);
        return next;
      });
    },
    [activeSlug],
  );

  const refresh = useCallback(async () => {
    const result = await refetch();
    return result.data ?? [];
  }, [refetch]);

  const activeTenant = useMemo(
    () => tenants.find((t) => t.slug === activeSlug) ?? null,
    [tenants, activeSlug],
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      tenants,
      activeTenant,
      hasWorkspace: tenants.length > 0,
      isLoading,
      error: error instanceof Error ? error.message : null,
      setActiveTenant,
      refresh,
    }),
    [tenants, activeTenant, isLoading, error, setActiveTenant, refresh],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within <TenantProvider>');
  return ctx;
}
